package com.devsusana.portfolio.components.widgets

import androidx.compose.runtime.Composable
import com.devsusana.portfolio.i18n.AppLanguage
import com.devsusana.portfolio.i18n.LanguageState
import com.devsusana.portfolio.styles.SitePalette
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.silk.components.forms.Button
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import org.jetbrains.compose.web.css.cssRem
import org.jetbrains.compose.web.css.px

/**
 * Interactive button to toggle between English and Spanish.
 * Uses a segmented style displaying both EN and ES with the active language highlighted.
 */
@Composable
fun LanguageToggle(modifier: Modifier = Modifier) {
    val currentLang = LanguageState.currentLanguage
    val colorMode = ColorMode.current
    val ctx = rememberPageContext()

    Row(
        modifier = modifier
            .borderRadius(8.px)
            .border(1.px, org.jetbrains.compose.web.css.LineStyle.Solid, SitePalette.surfaceBorder(colorMode))
            .backgroundColor(SitePalette.surface(colorMode))
            .padding(2.px),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(2.px)
    ) {
        LanguageOptionPill(
            language = AppLanguage.EN,
            isActive = currentLang == AppLanguage.EN,
            colorMode = colorMode,
            onClick = {
                LanguageState.setLanguage(AppLanguage.EN)
                try {
                    val path = ctx.route.path
                    if (path.startsWith("/posts/es/")) {
                        ctx.router.navigateTo(path.replace("/posts/es/", "/posts/"))
                    }
                } catch (_: Throwable) {
                    val path = kotlinx.browser.window.location.pathname
                    if (path.startsWith("/posts/es/")) {
                        kotlinx.browser.window.location.href = path.replace("/posts/es/", "/posts/")
                    }
                }
            }
        )

        LanguageOptionPill(
            language = AppLanguage.ES,
            isActive = currentLang == AppLanguage.ES,
            colorMode = colorMode,
            onClick = {
                LanguageState.setLanguage(AppLanguage.ES)
                try {
                    val path = ctx.route.path
                    if (path.startsWith("/posts/") && !path.startsWith("/posts/es/")) {
                        ctx.router.navigateTo(path.replace("/posts/", "/posts/es/"))
                    }
                } catch (_: Throwable) {
                    val path = kotlinx.browser.window.location.pathname
                    if (path.startsWith("/posts/") && !path.startsWith("/posts/es/")) {
                        kotlinx.browser.window.location.href = path.replace("/posts/", "/posts/es/")
                    }
                }
            }
        )
    }
}

@Composable
private fun LanguageOptionPill(
    language: AppLanguage,
    isActive: Boolean,
    colorMode: ColorMode,
    onClick: () -> Unit
) {
    Box(
        modifier = Modifier
            .borderRadius(6.px)
            .backgroundColor(
                if (isActive) {
                    if (colorMode.isDark) com.varabyte.kobweb.compose.ui.graphics.Color.rgba(61, 220, 132, 0.25f)
                    else com.varabyte.kobweb.compose.ui.graphics.Color.rgba(61, 220, 132, 0.2f)
                } else {
                    com.varabyte.kobweb.compose.ui.graphics.Color.rgba(0, 0, 0, 0f)
                }
            )
            .padding(leftRight = 8.px, topBottom = 4.px)
            .cursor(Cursor.Pointer)
            .onClick { onClick() },
        contentAlignment = Alignment.Center
    ) {
        SpanText(
            text = "${language.flag} ${language.code.uppercase()}",
            modifier = Modifier
                .fontSize(0.8.cssRem)
                .fontWeight(if (isActive) FontWeight.Bold else FontWeight.Medium)
                .color(
                    if (isActive) SitePalette.androidGreen
                    else SitePalette.textSecondary(colorMode)
                )
        )
    }
}
